import { IssueProduct } from '@gooduncles/gu-app-schema';
import { Button, Input, InputNumber, Popconfirm, Skeleton } from 'antd';
import { FC, useState } from 'react';

import useSWRProductInventory from 'src/hooks/swr/useSWRProductInventory';

import styles from './IssueTaskInventoryProductItem.module.scss';

interface IssueTaskInventoryProductItemProps {
  /** 상품 정보 */
  product: IssueProduct;
  /** 재고 처리 상태 정보 */
  productStatus: {
    status: 'pending' | 'completed' | 'skipped' | 'failed';
    inventoryIssueId?: string | null;
    processedAt?: string | null;
  };
  /** 매장명 */
  storeName: string;
  /** 편집 가능 여부 */
  editable: boolean;
  /** 입고 처리 시작 핸들러 */
  onStartInbound?: (reason: string, quantity: number) => void;
  /** 폐기 처리 핸들러 */
  onSkip?: () => void;
}

/**
 * 회수 상품 정보 표시 컴포넌트
 * - Inventory 정보 우선 표시 (강조)
 * - 상품명은 보조 정보로 표시
 * - 수량 표시
 * - 상태별 UI 및 버튼
 */
const IssueTaskInventoryProductItem: FC<IssueTaskInventoryProductItemProps> = ({
  product,
  productStatus,
  storeName,
  editable,
  onStartInbound,
  onSkip,
}) => {
  const { inventory, isLoading, error } = useSWRProductInventory(product.inventoryId);
  const [reason, setReason] = useState(`${storeName} 회수`);
  const [quantity, setQuantity] = useState<number>(product.quantity);

  // 로딩 중
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSkeleton}>
          <Skeleton.Input active size='small' style={{ width: 200, height: 16 }} />
        </div>
      </div>
    );
  }

  // 에러 또는 inventory 없음
  if (error || !inventory) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.productInfo}>
            <p className={styles.productName}>{product.productName}</p> ({product.quantity}개) -{' '}
            <span className={styles.errorText}>재고 정보 없음</span>
          </div>
        </div>
      </div>
    );
  }

  // 정상 표시: inventory명 우선 (bold), 상품명 보조
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.productInfo}>
          <p className={styles.inventoryName}>
            {inventory.inventoryName} / {product.inventoryUnit}
          </p>
        </div>

        {/* pending 상태: 사유 입력 및 입고/폐기 버튼 */}
        {productStatus.status === 'pending' &&
          (editable ? (
            <div className={styles.controls}>
              <div className={styles.inputGroup}>
                <Input
                  size='small'
                  placeholder='이슈 내용'
                  addonBefore='내용'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={styles.reasonInput}
                />
              </div>
              <div className={styles.buttonGroup}>
                <InputNumber
                  size='small'
                  placeholder='입고 수량'
                  value={quantity}
                  onChange={(value) => setQuantity(value ?? product.quantity)}
                  min={0.01}
                  max={product.quantity}
                  step={1}
                  className={styles.quantityInput}
                  addonBefore='수량'
                />
                <Popconfirm title='입고 안함' onConfirm={onSkip} okText='입고 안함' cancelText='취소'>
                  <Button size='small'>입고 안함</Button>
                </Popconfirm>
                <Popconfirm
                  title='입고 처리'
                  onConfirm={() => onStartInbound?.(reason, quantity)}
                  okText='입고 처리'
                  cancelText='취소'>
                  <Button size='small' type='primary'>
                    입고 처리
                  </Button>
                </Popconfirm>
              </div>
            </div>
          ) : (
            <div className={`${styles.statusInfo} ${styles.statusPending}`}>
              <span>입고 처리 중</span>
            </div>
          ))}

        {/* completed 상태: 입고 완료 정보 */}
        {productStatus.status === 'completed' && (
          <div className={`${styles.statusInfo} ${styles.statusCompleted}`}>
            <span>✅ 입고 완료</span>
            {productStatus.processedAt && (
              <span className={styles.statusDetail}>처리: {productStatus.processedAt}</span>
            )}
          </div>
        )}

        {/* skipped 상태: 폐기 정보 */}
        {productStatus.status === 'skipped' && (
          <div className={`${styles.statusInfo} ${styles.statusSkipped}`}>
            <span>⛔ 입고 안함</span>
            {productStatus.processedAt && (
              <span className={styles.statusDetail}>처리: {productStatus.processedAt}</span>
            )}
          </div>
        )}

        {/* failed 상태: 실패 정보 */}
        {productStatus.status === 'failed' && (
          <div className={`${styles.statusInfo} ${styles.statusFailed}`}>
            <span>❌ 처리 실패</span>
            {productStatus.processedAt && (
              <span className={styles.statusDetail}>시각: {productStatus.processedAt}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueTaskInventoryProductItem;
